import { DateTime } from "luxon";
import { isMarketHoliday, marketHolidayLabel } from "@/helpers/holidayUtilities";
import { webinarKey } from "@/types/products/product-definitions";

import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";

const logLevel: LogLevels = LogLevels.ERROR;
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( logLevel ) );

/*************************** DATE/TIME Constants *************************/
const MonthDayYearDateFormat: string = 'MMMM d, yyyy';
const DayMonthYearMinimalDateFormat: string = 'ddMMyy';
const YearMonthDayMinimalDateFormat: string = 'yyMMdd';

/*

Reference to the native DateTime function for creating a DateTime in UTC

Examples:
    DateTime. utc()                                            //~> now
    DateTime. utc(2017)                                        //~> 2017-01-01T00:00:00Z
    DateTime. utc(2017, 3)                                     //~> 2017-03-01T00:00:00Z
    DateTime. utc(2017, 3, 12)                                 //~> 2017-03-12T00:00:00Z
    DateTime. utc(2017, 3, 12, 5)                              //~> 2017-03-12T05:00:00Z
    DateTime. utc(2017, 3, 12, 5, 45)                          //~> 2017-03-12T05:45:00Z
    DateTime. utc(2017, 3, 12, 5, 45, { locale: "fr" } )       //~> 2017-03-12T05:45:00Z with a French locale
    DateTime. utc(2017, 3, 12, 5, 45, 10)                      //~> 2017-03-12T05:45:10Z
    DateTime. utc(2017, 3, 12, 5, 45, 10, 765, { locale: "fr") //~> 2017-03-12T05:45:10.765Z with a French locale

Params:
    year – — The calendar year. If omitted (as in, call utc() with no arguments), the current time will be used
    month – — The month, 1-indexed
    day – — The day of the month
    hour – — The hour of the day, in 24-hour time
    minute – — The minute of the hour, meaning a number between 0 and 59
    second – — The second of the minute, meaning a number between 0 and 59
    millisecond – — The millisecond of the second, meaning a number between 0 and 999
    options – — configuration options for the DateTime
    Properties:
    locale – — a locale to set on the resulting DateTime instance
    outputCalendar – — the output calendar to set on the resulting DateTime instance
    numberingSystem – — the numbering system to set on the resulting DateTime instance
 */

/**
 * A type definition for simplifying the API of the IDateTimeService
 */
export type DateTimeLike = DateTime | Date | number | string | undefined;

/**
 *
 * @param value
 */
export function normalizeDateTime( value: DateTimeLike | undefined | null ): DateTime {
    let date: DateTime | null = null;

    const valueType: string = typeof value;
    logger.debug( `[ dateTimeService ] valueType: ${ valueType }` );

    // If the value passed in is not defined, then use the current date
    if ( valueType === 'undefined' ) {
        if ( LogLevels.DEBUG === logLevel ) {
            logger.debug( `[ dateTimeService ] - valueType undefined, selecting current date/time` );
            console.trace();
        }

        // If nothing was provided, assign the current date & time (in UTC)
        date = DateTime.utc();
    }
    // If the value passed in is a string, attempt to parse it using standard formats
    else if ( valueType === 'string' ) {
        const dateString: string = value as string;
        logger.debug( `[ dateTimeService ] - valueType is string` );

        // Attempt to parse using the most likely format and move down the list (ISO, RFC2822, HTTP header, give up)
        const isoDate: DateTime = DateTime.fromISO( dateString, { setZone: true } );
        logger.debug( `[ dateTimeService ] - parsed as ISO date: ${ isoDate }` );

        // If the string was successfully parsed as an ISO-compliant string, return it
        if ( isoDate.isValid ) {
            logger.debug( `[ dateTimeService ] - is valid ISO date` );
            date = isoDate;
        }
        // Otherwise, attempt to parse it as an RFC 2822 string
        else {
            // Attempt to parse using the standard US date format 'MMM dd, yyyy'
            const monthDayYearDateFormat: DateTime = DateTime.fromFormat( dateString, MonthDayYearDateFormat, { setZone: true } );
            logger.debug( `[ dateTimeService ] - parsed as MMMM dd, yyyy date: ${ monthDayYearDateFormat }` );

            if ( monthDayYearDateFormat.isValid ) {
                logger.debug( `[ dateTimeService ] - is valid "MMMM dd, yyyy" date` );
                date = monthDayYearDateFormat;
            } else {
                logger.debug( `[ dateTimeService ] - attempting to parse as ISO` );
                const isoDate: DateTime = DateTime.fromISO( dateString, { setZone: true } );
                logger.debug( `[ dateTimeService ] - as ISO: '${ isoDate }'` );
                // If the string was successfully parsed as an RFC2822-compliant string, return it
                if ( isoDate.isValid ) {
                    logger.debug( `[ dateTimeService ] - is valid ISO date` );
                    date = isoDate;
                } else {
                    logger.debug( `[ dateTimeService ] - attempting to parse as RFC 2822` );
                    const rfc2822Date: DateTime = DateTime.fromRFC2822( dateString, { setZone: true } );
                    logger.debug( `[ dateTimeService ] - as RFC 2822: '${ rfc2822Date }'` );
                    // If the string was successfully parsed as an RFC2822-compliant string, return it
                    if ( rfc2822Date.isValid ) {
                        logger.debug( `[ dateTimeService ] - is valid RFC2822 date` );
                        date = rfc2822Date;
                    }
                    // Otherwise, attempt to parse it as an HTTP header formatted string
                    else {
                        logger.debug( `[ dateTimeService ] - attempting to parse in HTTP header date format` );
                        const httpHeaderDate: DateTime = DateTime.fromHTTP( dateString, { setZone: true } );
                        logger.debug( `[ dateTimeService ] - HTTP Header date: '${ httpHeaderDate }'` );
                        // If the string was successfully parsed as an HTTP-compliant string, return it
                        if ( httpHeaderDate.isValid ) {
                            logger.debug( `[ dateTimeService ] - is valid HTTP header date` );
                            date = httpHeaderDate;
                        }
                        // else - give up and return the original explicit 'null' value for the date
                        logger.debug( `[ dateTimeService ] - FUCK IF I KNOW...` );
                    }
                }
            }
        }
    }
    else if ( valueType === 'number' ) {
        logger.debug( `[ dateTimeService ] - valueType was number, attempting to parse as timestamp value` );
        const dateFromTimestamp: DateTime = DateTime.fromMillis( value as number ); // .setZone( EasternTimeZone );
        logger.debug( `[ dateTimeService ] - date from timestamp: '${ dateFromTimestamp }'` );

        // If the value resolves to a valid DateTime, return it
        if ( dateFromTimestamp.isValid ) {
            logger.debug( `[ dateTimeService ] - date from timestamp is valid` );
            date = dateFromTimestamp;
        }
        // else - give up and return the original explicit 'null' value for the date
    }
    // Test to determine if the object passed in is an instance of the native JavaScript Date object
    else if ( value instanceof Date ) {
        logger.debug( `value is instanceof JavaScript Date` );
        date = DateTime.fromJSDate( value ); // .setZone( EasternTimeZone );
        logger.debug( `JSDate value: '${ date }'` );
    }
    // Test to determine if the object passed in is already an instance of the DateTime object
    else if ( value instanceof DateTime || DateTime.isDateTime( value ) ) {
        logger.debug( `[ dateTimeService ] - value is instanceof DateTime: [ y:${ value.year }, m:${ value.month }, d:${ value.day }, h:${ value.hour }, m:${ value.minute }, s:${ value.second }, z:${ value.zoneName } ]` );
        date = value; // .setZone( EasternTimeZone );
    }
    else {
        date = DateTime.fromObject( { year: 0, month: 0, day: 0 } );
    }

    logger.debug( `[ dateTimeService ] - final value: '${ date }'` );

    return date!;
}

export function fromSortableString( value: string ): DateTime {
    const SortableDateFormat: string = 'yyyy-MM-dd';
    return DateTime.fromFormat( value, SortableDateFormat );
}

export function utcNow(): DateTime {
    return DateTime.utc();
}

export function localNow(): DateTime {
    return DateTime.local();
}

/*----------------------------------------------------------------------------------------------- */
/* ------------------------------------ FORMATTING FUNCTIONS ------------------------------------ */
/*----------------------------------------------------------------------------------------------- */

export function formatDateAsSortableString( value: DateTimeLike, isNormalized: boolean = false ): string {
    const SortableDateFormat: string = 'yyyy-MM-dd';
    logger.debug( `[ dateTimeService ] starting value (sortable): '${ value }'` );

    let dt: DateTime;
    if ( isNormalized ) {
        dt = value as DateTime;
    } else {
        dt = normalizeDateTime( value );
        logger.debug( `[ dateTimeService ] normalized value: '${ dt?.isValid ? dt.toFormat( SortableDateFormat ) : '' }'` );
    }

    return dt?.isValid ? dt.toFormat( SortableDateFormat ) : '';
}

export function formatDateAsMonthDayYearString( value: DateTimeLike | undefined | null, isNormalized: boolean = false ): string {
    logger.debug( `[ dateTimeService ] starting value (MMMM dd, yyyy): '${ value }'` );

    let dt: DateTime;
    if ( isNormalized ) {
        dt = value as DateTime;
    } else {
        dt = normalizeDateTime( value );
        logger.debug( `[ dateTimeService ] normalized value: '${ dt.isValid ? dt.toFormat( MonthDayYearDateFormat ) : '' }'` );
    }

    return dt.isValid ? dt.toFormat( MonthDayYearDateFormat ) : '';
}

export function formatDateAsISOString( value: DateTimeLike, isNormalized: boolean = false ): string {
    logger.debug( `[ dateTimeService ] starting value (ISO): '${ value }'` );

    let dt: DateTime;
    if ( isNormalized ) {
        dt = value as DateTime;
    } else {
        dt = normalizeDateTime( value );
        logger.debug( `[ dateTimeService ] normalized value: '`, dt?.isValid ? dt.toISODate() : '', `'` );
    }

    return dt?.isValid ? dt.toISODate()! : '';
}

export function formatDateAsYearMonthDayMinimalString( value: DateTimeLike, isNormalized: boolean = false ): string {
    logger.debug( `[ dateTimeService ] starting value (yyMMdd): '${ value }'` );

    let dt: DateTime;
    if ( isNormalized ) {
        dt = value as DateTime;
    } else {
        dt = normalizeDateTime( value );
        logger.debug( `[ dateTimeService ] normalized value: '${ dt.isValid ? dt.toFormat( YearMonthDayMinimalDateFormat ) : '' }'` );
    }

    return dt.isValid ? dt.toFormat( YearMonthDayMinimalDateFormat ) : '';
}

export function formatDateAsDayMonthYearMinimalString( date?: DateTimeLike, isNormalized: boolean = false ): string {
    logger.debug( `[ dateTimeService ] starting value (ddMMyy): '${ date }'` );

    let dt: DateTime;
    if ( isNormalized ) {
        dt = date as DateTime ?? DateTime.now();
    } else {
        dt = normalizeDateTime( date ?? DateTime.now() );
        logger.debug( `[ dateTimeService ] normalized value: '${ dt.isValid ? dt.toFormat( DayMonthYearMinimalDateFormat ) : '' }'` );
    }

    return dt.isValid ? dt.toFormat( DayMonthYearMinimalDateFormat ) : '';
}

export function formatDateAsISO8601String( value: DateTimeLike, isNormalized: boolean = false ): string {
    logger.debug( `[ dateTimeService ] starting value (ISO): '${ value }'` );

    let dt: DateTime;
    if ( isNormalized ) {
        dt = value as DateTime;
    } else {
        dt = normalizeDateTime( value );
        logger.debug( `[ dateTimeService ] normalized value: '${ dt?.isValid ? dt.toISODate() : '' }'` );
    }

    return dt?.isValid ? `${ dt.toISODate()! }${ dt.toISOTime( { includePrefix: true } ) }` : '';
}

export function mostRecentValidMarketDateAsSortableString( startingDateValue?: DateTimeLike ): string {
    const startOfCurrentDay: DateTime = DateTime.utc();
    // const startOfCurrentDay: DateTime = DateTime.now().setZone( EasternTimeZone ).startOf( 'day' );
    const startingDateTime: DateTime = !!( startingDateValue ) ? normalizeDateTime( startingDateValue ) : startOfCurrentDay;

    logger.info( `[ dateTimeService ] - retrieving a valid date for '${ startingDateTime }'` );
    return formatDateAsSortableString( mostRecentValidMarketDate( startingDateTime ) );
}

export function formatDateAsDayOfWeekMonthDayYearString( value: DateTimeLike | undefined | null, isNormalized: boolean = false ): string {
    logger.debug( `[ dateTimeService ] starting value (dow): '${ value }'` );

    let dt: DateTime;
    if ( isNormalized ) {
        dt = value as DateTime;
    } else {
        dt = normalizeDateTime( value );
        logger.debug( `[ dateTimeService ] normalized value: '${ dt.isValid ? dt.toLocaleString( DateTime.DATE_MED_WITH_WEEKDAY ) : '' }'` );
    }

    return dt.isValid ? dt.toLocaleString( DateTime.DATE_MED_WITH_WEEKDAY ) : '';
}

/*----------------------------------------------------------------------------------------------- */
/* ----------------------------------- CURRENT DATE FUNCTIONS ----------------------------------- */
/*----------------------------------------------------------------------------------------------- */

const closingHour: number = 2;
export function mostRecentValidMarketDate( startingDateValue: DateTimeLike ): DateTime {
    logger.debug( `[ dateTimeService ] - starting with '${ startingDateValue }'` );

    const startOfCurrentDay: DateTime = DateTime.utc();
    // const startOfCurrentDay: DateTime = DateTime.now().setZone( EasternTimeZone ).startOf( 'day' );
    // Intentionally not performing a "strict" check vs null to cover both null AND undefined
    if ( null == startingDateValue || typeof startingDateValue === 'undefined' ) {
        logger.debug( `[ dateTimeService ] - was null, selecting the current date/time` );
        startingDateValue = DateTime.utc();
        // startingDateValue = DateTime.now().setZone( EasternTimeZone );
    }

    let potentialMostRecentValidMarketDate: DateTime | null = normalizeDateTime( startingDateValue );
    logger.debug( `[ dateTimeService ] - potential (normalized): '${ potentialMostRecentValidMarketDate }'` );
    // If the original starting date does not correctly resolve to a DateTime, then return an invalid Date object
    if ( null === potentialMostRecentValidMarketDate ) {
        return DateTime.fromJSDate( new Date( Number.NaN ) );
    }

    // ONLY VALID FOR THE CURRENT DAY
    if ( startOfCurrentDay <= potentialMostRecentValidMarketDate.startOf( 'day' ) ) {
        const currentHour: number = potentialMostRecentValidMarketDate.hour;
        // if the hour is before the closing of the market, go back one day
        if ( currentHour < closingHour ) {
            logger.debug( `current hour is before expected data availability (11pm Eastern) : ${ currentHour }` );
            potentialMostRecentValidMarketDate = potentialMostRecentValidMarketDate.plus( { days: -1 } );
            logger.debug( `updated potential market date: '${ potentialMostRecentValidMarketDate }'` );
        }
    }

    let isValid: boolean = false;
    while ( !isValid ) {
        // Filter out all Saturdays (weekday = 6) & Sundays (weekday = 7)
        while ( potentialMostRecentValidMarketDate.weekday > 5 ) {
            logger.debug( `[ dateTimeService ] - current DOW is part of a weekend - Saturdays (weekday = 6) & Sundays (weekday = 7): ${ potentialMostRecentValidMarketDate.weekday }` );
            // Walk the date backwards by one day
            potentialMostRecentValidMarketDate = potentialMostRecentValidMarketDate.plus( { days: -1 } );
            logger.debug( `[ dateTimeService ] - updated potential market date: '${ potentialMostRecentValidMarketDate }'` );
        }

        // Filter out all market holidays
        let dateString: string = formatDateAsSortableString( potentialMostRecentValidMarketDate, true );
        while ( isMarketHoliday( dateString ) ) {
            logger.debug( `[ dateTimeService ] - current date is a market holiday: '${ marketHolidayLabel( dateString ) }'` );
            // Walk the date backwards by one day
            potentialMostRecentValidMarketDate = potentialMostRecentValidMarketDate.plus( { days: -1 } );
            dateString = formatDateAsSortableString( potentialMostRecentValidMarketDate, true );

            logger.debug( `[ dateTimeService ] - updated potential market date: '${ potentialMostRecentValidMarketDate }'` );
        }

        // Check to determine that we haven't rolled into a weekend before a Holiday (since we're walking backwards in time)
        const isNotAWeekend: boolean = potentialMostRecentValidMarketDate.weekday <= 5;
        const isNotAMarketHoliday: boolean = !isMarketHoliday( dateString );
        isValid = isNotAWeekend && isNotAMarketHoliday;

        logger.debug( `[ dateTimeService ] - valid market date: ${ potentialMostRecentValidMarketDate } ==> ${ isValid }` );
    }

    const finalDateTime: DateTime = DateTime.fromObject( {
        year: potentialMostRecentValidMarketDate?.year,
        month: potentialMostRecentValidMarketDate?.month,
        day: potentialMostRecentValidMarketDate?.day
    } );
    // }, { zone: EasternTimeZone } );

    logger.debug( `market date returned: ${ finalDateTime }` );

    return finalDateTime;
}

export function maxDateTime( topic?: string | undefined ): DateTime {
    let maxDate = utcNow();
    if ( topic && webinarKey === topic ) {
        maxDate = maxDate.plus( { weeks: 3 } );
    }

    return maxDate;
}

export function countTime( html: string ): string {
    const domElement: HTMLDivElement = document.createElement( 'div' );
    domElement.innerHTML = html;

    const charNum: number = domElement.innerText.replace( /\s+/g, '' ).length;
    const imgNum: number = domElement.querySelectorAll( 'img' ).length;

    const timeForRound: number = 0.2;
    const time: number = Math.round( charNum / 1500 + imgNum * 0.2 + timeForRound );
    const parseTime: number = time < 1 ? 1 : time;
    return `${ parseTime } Minute Read`;
}
